import firebase from "firebase/app";
import { auth, db } from "./config";

/**
 * Counters for how often handymen/properties are looked at and how often their
 * contact details are actually used — a web port of the app's Database/analytics.js
 * (handymen) and Database/propertyAnalytics.js (properties). Same collections
 * (`handymanStats` / `propertyStats`), so counts stay unified across app and website.
 *
 * Analytics only: a failure here must never interrupt what the visitor was doing, so
 * every write is fire-and-forget and only warns on error.
 */

export const STATS_COLLECTION = "handymanStats";
export const PROPERTY_STATS_COLLECTION = "propertyStats";

// ContactGate only ever renders Call/WhatsApp/Email buttons (no SMS on the
// web), for both handymen and properties — the app's own SMS channel for
// properties (Database/propertyAnalytics.js) writes a different field
// (smsTaps) into the same propertyStats document, which is fine: both feed
// the same profileViews/contactViews totals the dashboard actually reads.
export const CONTACT_CHANNELS = { CALL: "call", WHATSAPP: "whatsapp", EMAIL: "email" };

const CHANNEL_FIELD = {
  [CONTACT_CHANNELS.CALL]: "callTaps",
  [CONTACT_CHANNELS.WHATSAPP]: "whatsappTaps",
  [CONTACT_CHANNELS.EMAIL]: "emailTaps",
};

const bumpIn = async (collectionName, idField, id, fields) => {
  if (!id || !auth.currentUser) return;

  try {
    await db
      .collection(collectionName)
      .doc(id)
      .set(
        { [idField]: id, ...fields, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
        { merge: true }
      );
  } catch (e) {
    console.warn(`Could not record ${collectionName} stats:`, e);
  }
};

const bump = (handymanId, fields) => bumpIn(STATS_COLLECTION, "handymanId", handymanId, fields);
const bumpProperty = (propertyId, fields) => bumpIn(PROPERTY_STATS_COLLECTION, "propertyId", propertyId, fields);

/** Someone opened this handyman's protected contact card. */
export const recordProfileView = (handymanId) =>
  bump(handymanId, { profileViews: firebase.firestore.FieldValue.increment(1) });

/** Someone pressed call / WhatsApp / email for this handyman. */
export const recordContactView = (handymanId, channel) => {
  const field = CHANNEL_FIELD[channel];
  if (!field) return Promise.resolve();

  return bump(handymanId, {
    contactViews: firebase.firestore.FieldValue.increment(1),
    [field]: firebase.firestore.FieldValue.increment(1),
  });
};

/** Someone opened this property's protected contact card. */
export const recordPropertyProfileView = (propertyId) =>
  bumpProperty(propertyId, { profileViews: firebase.firestore.FieldValue.increment(1) });

/** Someone pressed call / WhatsApp / email about this property. */
export const recordPropertyContactView = (propertyId, channel) => {
  const field = CHANNEL_FIELD[channel];
  if (!field) return Promise.resolve();

  return bumpProperty(propertyId, {
    contactViews: firebase.firestore.FieldValue.increment(1),
    [field]: firebase.firestore.FieldValue.increment(1),
  });
};
